import { useState, useEffect, useRef } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  orderBy,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Franchise, Branch, Barber, QueueTicket } from '@/types';

interface UsePublicQueueDataProps {
  franchiseId: string;
  branchId: string | null;
}

interface UsePublicQueueDataReturn {
  franchise: Franchise | null;
  branches: Branch[];
  barbers: Barber[];
  tickets: QueueTicket[];
  loading: boolean;
  error: string | null;
  isReconnecting: boolean;
}

export function usePublicQueueData({
  franchiseId,
  branchId,
}: UsePublicQueueDataProps): UsePublicQueueDataReturn {
  const [franchise, setFranchise] = useState<Franchise | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [tickets, setTickets] = useState<QueueTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const unsubscribesRef = useRef<(() => void)[]>([]);

  // Subscribe to franchise info (real-time)
  useEffect(() => {
    if (!franchiseId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'franchises', franchiseId),
      (snap) => {
        if (!snap.exists()) {
          setError('franchise_not_found');
          setLoading(false);
          return;
        }
        setFranchise({
          ...snap.data(),
          franchiseId: snap.id,
        } as Franchise);
        setIsReconnecting(false);
      },
      (err) => {
        console.error('Error subscribing to franchise:', err);
        setError('load_error');
        setIsReconnecting(true);
      }
    );

    unsubscribesRef.current.push(unsubscribe);

    return () => {
      unsubscribe();
      unsubscribesRef.current = unsubscribesRef.current.filter(
        (u) => u !== unsubscribe
      );
    };
  }, [franchiseId]);

  // Subscribe to branches (real-time)
  useEffect(() => {
    if (!franchiseId) return;

    const branchesQuery = query(
      collection(db, 'branches'),
      where('franchiseId', '==', franchiseId),
      where('isActive', '==', true)
    );

    const unsubscribe = onSnapshot(
      branchesQuery,
      (snapshot) => {
        const branchList = snapshot.docs.map(
          (d) =>
            ({
              ...d.data(),
              branchId: d.id,
            }) as Branch
        );
        setBranches(branchList);
        setIsReconnecting(false);
      },
      (err) => {
        console.error('Error subscribing to branches:', err);
        setError('load_error');
        setIsReconnecting(true);
      }
    );

    unsubscribesRef.current.push(unsubscribe);

    return () => {
      unsubscribe();
      unsubscribesRef.current = unsubscribesRef.current.filter(
        (u) => u !== unsubscribe
      );
    };
  }, [franchiseId]);

  // Subscribe to barbers (real-time)
  useEffect(() => {
    if (!branchId) {
      setBarbers([]);
      return;
    }

    const barbersQuery = query(
      collection(db, 'barbers'),
      where('branchId', '==', branchId),
      where('isActive', '==', true)
    );

    const unsubscribe = onSnapshot(
      barbersQuery,
      (snapshot) => {
        const barberList = snapshot.docs.map(
          (d) =>
            ({
              ...d.data(),
              barberId: d.id,
            }) as Barber
        );
        setBarbers(barberList);
        setIsReconnecting(false);
      },
      (err) => {
        console.error('Error subscribing to barbers:', err);
        setIsReconnecting(true);
      }
    );

    unsubscribesRef.current.push(unsubscribe);

    return () => {
      unsubscribe();
      unsubscribesRef.current = unsubscribesRef.current.filter(
        (u) => u !== unsubscribe
      );
    };
  }, [branchId]);

  // Subscribe to queue tickets (real-time)
  useEffect(() => {
    if (!branchId) {
      setTickets([]);
      setLoading(false);
      return;
    }

    const ticketsQuery = query(
      collection(db, 'queues'),
      where('branchId', '==', branchId),
      where('status', 'in', [
        'waiting',
        'notified',
        'arrived',
        'in_service',
        'finishing',
        'waiting_overtime_approval',
      ]),
      orderBy('position', 'asc')
    );

    const unsubscribe = onSnapshot(
      ticketsQuery,
      (snapshot) => {
        const ticketList = snapshot.docs.map(
          (d) =>
            ({
              ...d.data(),
              queueId: d.id,
            }) as QueueTicket
        );
        setTickets(ticketList);
        setLoading(false);
        setIsReconnecting(false);
      },
      (err) => {
        console.error('Error subscribing to queue:', err);
        setIsReconnecting(true);
      }
    );

    unsubscribesRef.current.push(unsubscribe);

    return () => {
      unsubscribe();
      unsubscribesRef.current = unsubscribesRef.current.filter(
        (u) => u !== unsubscribe
      );
    };
  }, [branchId]);

  // Cleanup all subscriptions on unmount
  useEffect(() => {
    return () => {
      unsubscribesRef.current.forEach((unsub) => unsub());
      unsubscribesRef.current = [];
    };
  }, []);

  return {
    franchise,
    branches,
    barbers,
    tickets,
    loading,
    error,
    isReconnecting,
  };
}
